// Pure helpers for the stable `favotrip_cid` cookie. This is the
// cross-event identity signal the FE attaches to every server-side
// CAPI dispatch. Without it, Meta + TikTok use a different identifier
// per event (we previously sent the per-event UUID `eventId` as
// `external_id`), which means the platforms cannot link a single
// visitor's PageView → ViewContent → InitiateCheckout → Purchase
// trail. Result: low Event Match Quality + impossible cross-event
// frequency capping.
//
// Casper 2026-05-15 P1a directive — "we hebben die cross-event identity
// nodig voor een goede EMQ-score". 2-year max-age matches Meta's
// recommended `external_id` cookie window for first-party identifiers.
//
// SafeSite=Lax + path=/. NOT consent-gated — this is an anonymous
// pseudonymous identifier (no PII derivable; just a random UUID per
// browser). For EU bezoekers without marketing consent, the cookie
// still gets written client-side, but the server-side dispatch is
// already gated by the BE bypassConsent flag (set per Casper's
// 2026-05-13 GDPR risk-acceptance) — so the audit-trail correctly
// flags consent=false alongside the clientId, and customer-data law
// enforcement can review the policy after the fact.

const COOKIE_NAME = "favotrip_cid";
const COOKIE_MAX_AGE_SEC = 2 * 365 * 24 * 60 * 60; // 2 years
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Read the value of a single cookie from a `document.cookie`-style
 * string. Returns `undefined` when absent or malformed.
 *
 * @param {string} cookieString
 * @returns {string|undefined}
 */
export function readClientIdFromCookieString(cookieString) {
  if (typeof cookieString !== "string" || !cookieString) return undefined;
  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`),
  );
  if (!match) return undefined;
  let value;
  try {
    value = decodeURIComponent(match[1]);
  } catch {
    value = match[1];
  }
  return UUID_V4_PATTERN.test(value) ? value : undefined;
}

/**
 * Build the `Set-Cookie`-style attribute string for the client-id
 * cookie. Exposed so tests can pin the exact value the browser sees.
 *
 * @param {string} value validated UUID v4
 * @returns {string}
 */
export function buildClientIdCookieString(value) {
  return `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
}

/**
 * Generate a fresh UUID v4 using the supplied crypto-like object.
 * Falls back to a Math.random-based generator only if `randomUUID`
 * is unavailable (older browsers, exotic test environments).
 *
 * @param {{ randomUUID?: () => string }|null} cryptoLike
 * @returns {string}
 */
export function generateClientId(cryptoLike) {
  try {
    const c = cryptoLike ?? (typeof crypto !== "undefined" ? crypto : null);
    if (c && typeof c.randomUUID === "function") {
      return c.randomUUID();
    }
  } catch {
    // fall through to Math.random fallback
  }
  // RFC 4122 v4 fallback. Lower entropy than crypto.randomUUID but
  // sufficient for cross-event linking (collision probability with
  // 2^61 random pairs is still ~1e-18). Marker '4' + variant bits set.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Read or mint the stable client_id. If the cookie holds a valid
 * UUID v4, return it. Otherwise generate one + write the cookie via
 * the supplied side-effect callback (so tests can assert on the
 * exact write). Returns the resolved id either way.
 *
 * Pure-effect at the JS level — the React layer wraps this with the
 * actual `document.cookie` read/write.
 *
 * @param {object} params
 * @param {string} [params.cookieString] current `document.cookie`
 * @param {(cookie: string) => void} [params.writeCookie] cookie-write side-effect
 * @param {{ randomUUID?: () => string }} [params.cryptoLike] used by generateClientId
 * @returns {string}
 */
export function resolveClientId({
  cookieString,
  writeCookie,
  cryptoLike,
} = {}) {
  const existing = readClientIdFromCookieString(cookieString);
  if (existing) return existing;
  const fresh = generateClientId(cryptoLike);
  if (typeof writeCookie === "function") {
    writeCookie(buildClientIdCookieString(fresh));
  }
  return fresh;
}

/**
 * Browser-side convenience: read or mint the cookie via the real
 * `document` global. No-op (returns `undefined`) on the server.
 *
 * @returns {string|undefined}
 */
export function ensureClientIdInBrowser() {
  if (typeof document === "undefined") return undefined;
  return resolveClientId({
    cookieString: document.cookie,
    writeCookie: (s) => {
      try {
        document.cookie = s;
      } catch {
        // Private-browsing / locked-down storage: silently swallow.
        // We still return the generated id so the FE→BE dispatch has
        // SOMETHING (one-shot session id) rather than nothing.
      }
    },
  });
}

// Exported for tests to assert the constant directly.
export const __INTERNALS = {
  COOKIE_NAME,
  COOKIE_MAX_AGE_SEC,
  UUID_V4_PATTERN,
};
